import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { HttpClient, HttpParams } from '@angular/common/http';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
    BestellungExtern,
    BestellungExternDokument,
    BestellungExternDokumentPruefung,
    BestellungExternPosition,
} from '../bestellung-extern';
import { BestellungExternService } from '../bestellung-extern.service';
import { BestellungExternDetailsComponent } from '../bestellung-extern-details/bestellung-extern-details.component';
import { GemeinkostenInvoiceKiService } from '../../gemeinkosten/gemeinkosten-invoice-review/gemeinkosten-invoice-ki.service';
import { WawiInvoiceDraftDto } from '../../gemeinkosten/gemeinkosten-invoice-review/gemeinkosten-invoice.models';
import { NebenkostenPrimanotaVorschauDialogComponent } from '../../gemeinkosten/gemeinkosten-invoice-review/nebenkosten-primanota-vorschau-dialog.component';
import {
    PrimanotaExportVorschauData,
    PrimanotaExportVorschauVariante,
    PrimanotaExportVorschauZeile,
} from '../../gemeinkosten/gemeinkosten-invoice-review/nebenkosten-primanota-vorschau-dialog.models';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { LocalStorageService } from '../../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { KieneBackendApiService } from '../../../../../projects/kiene-core/src/lib/services/backend-api/kiene-backend-api.service';
import { KieneUtils } from '../../../../../projects/kiene-core/src/lib/utils/KieneUtils';
import { ConfirmDialogComponent } from '../../../../../projects/kiene-core/src/lib/components/dialogs/confirm-dialog/confirm-dialog.component';
import {
    ConfirmDialogInput,
    ConfirmDialogResult,
} from '../../../../../projects/kiene-core/src/lib/components/dialogs/confirm-dialog/confirm-dialog';
import { EnterValueDialogComponent } from '../../../../../projects/kiene-core/src/lib/components/dialogs/enter-value-dialog/enter-value-dialog.component';
import {
    EnterValueInput,
    EnterValueResponse,
} from '../../../../../projects/kiene-core/src/lib/components/dialogs/enter-value-dialog/enter-value-response';
import {
    einkaufAufwandskonto,
    isEinkaufVerbrauchArticle,
    isFibuEinkaufArtikelAbAktiv,
} from '../../../warehouse/material/material';

const OPEN_ORDER_STATUS = [23, 24, 25, 27, 29];

export type WareneingangScanKind = 'deliveryNote' | 'invoice' | 'credit';

export interface InvoiceDeliverNoteDialogData {
    files?: File[];
    bestellung?: BestellungExtern | null;
    candidates?: BestellungExtern[];
    /** Vom Menschen vor dem Upload gewählt – OCR überschreibt das nicht. */
    preferredKind?: WareneingangScanKind;
    /** Schon hinterlegte Rechnung: direkt die Prüfung, ohne neuen Scan. */
    dokument?: BestellungExternDokument | null;
}

export interface InvoiceDeliverNoteSavedResult {
    saved: true;
    kind: WareneingangScanKind;
    extraFiles: File[];
    bestellung?: BestellungExtern | null;
    /** Nach Rechnung-Persist: Beleg für Positionsprüfung. */
    bedId?: number;
    dokument?: BestellungExternDokument | null;
}

export interface InvoiceDeliverNoteManualResult {
    saved: false;
    kind: WareneingangScanKind;
    extraFiles: File[];
    bestellung: BestellungExtern;
}

export interface WareneingangMatchRow {
    artikel: string;
    mengeSoll: number;
    mengeIst: number;
    ocrMenge: number | null;
    matched: boolean;
    qtyOk: boolean;
}

@Component({
    selector: 'kiene-invoice-deliver-note-dialog',
    templateUrl: './invoice-deliver-note-dialog.component.html',
    styleUrls: ['./invoice-deliver-note-dialog.component.scss'],
})
export class InvoiceDeliverNoteDialogComponent implements OnInit, OnDestroy {
    /** assign = Typ/Bestellung; invoiceReview = Positionsabgleich wie betriebliche Kosten */
    phase: 'assign' | 'invoiceReview' = 'assign';
    scanning = false;
    saving = false;
    scanFailed = false;
    scanError = '';
    selectedKind: WareneingangScanKind = 'deliveryNote';
    suggestedKind: WareneingangScanKind = 'deliveryNote';
    /** Manuell geklickt. Der Dateiname darf das danach nicht mehr umbiegen. */
    private chosenKind: WareneingangScanKind | null = null;
    kindLocked = false;
    draft: WawiInvoiceDraftDto = null;
    matches: WareneingangMatchRow[] = [];
    warnings: string[] = [];
    scanInfoOpen = false;
    resolvedBestellung: BestellungExtern | null = null;
    selectedBestellungId: number | null = null;
    lieferantCandidates: BestellungExtern[] = [];
    /** Trefferzahl OCR-Artikel ↔ Bestellpositionen (für Sortierung). */
    private candidateScores = new Map<number, number>();
    private ordersLoadSeq = 0;

    /** Rechnung-Review (nach Persist) */
    invoiceDokument: BestellungExternDokument | null = null;
    invoicePruefung: BestellungExternDokumentPruefung | null = null;
    inRechnungCtrls: FormControl[] = [];
    /** Separat wie früher: Freigabe für Primanota (`geprueft`). */
    geprueftCtrls: FormControl[] = [];
    rechnungsNummerCtrl = new FormControl<string | null>(null, [Validators.required]);
    rechnungsDatumCtrl = new FormControl<Date | string | null>(null, [Validators.required]);
    /** Kontrolle wie bei betrieblichen Kosten: Summen vom Beleg eintippen */
    kontrollNetto: number | null = null;
    kontrollMwst: number | null = null;
    kontrollBrutto: number | null = null;
    /** Nutzer hat Netto/MwSt/Brutto selbst geändert – dann nicht mehr überschreiben. */
    private summenManuell = false;
    /** Lieferant-Gegenkonto für Aufwandskonto-Fallback (optional). */
    lieferantGegenkonto: string | null = null;
    /** Kreditor-Konto am Lieferanten (Primanota-Gegenkonto). */
    lieferantKreditor: string | null = null;
    stammSavingBeaId: number | null = null;

    pdfSrc: Uint8Array | null = null;
    pdfObjectUrl: string | null = null;
    page = 1;
    totalPages = 0;
    pdfLoading = false;
    isAdmin = false;

    constructor(
        public dialogRef: MatDialogRef<
            InvoiceDeliverNoteDialogComponent,
            InvoiceDeliverNoteSavedResult | InvoiceDeliverNoteManualResult | undefined
        >,
        @Inject(MAT_DIALOG_DATA) public data: InvoiceDeliverNoteDialogData,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly invoiceKi: GemeinkostenInvoiceKiService,
        private readonly api: KieneBackendApiService,
        private readonly bestellungExternService: BestellungExternService,
        private readonly messageService: MessageService,
        private readonly localStorageService: LocalStorageService,
        private readonly clipboard: Clipboard,
        private readonly dialog: MatDialog,
        private readonly http: HttpClient,
    ) {
        this.isAdmin = this.localStorageService.currentUserIsAdmin();
    }

    get file(): File | null {
        return this.data?.files?.[0] ?? null;
    }

    get extraFiles(): File[] {
        return (this.data?.files ?? []).slice(1);
    }

    get bestellung(): BestellungExtern | null {
        return this.resolvedBestellung || this.data?.bestellung || null;
    }

    get belegId(): string {
        const id = this.draft?.runId;
        return id != null && String(id).trim() !== '' ? String(id) : '–';
    }

    get assignmentOptions(): BestellungExtern[] {
        const list = [...this.lieferantCandidates, ...(this.data?.candidates || [])];
        const seen = new Set<number>();
        const unique: BestellungExtern[] = [];
        for (const c of list) {
            const id = Number(c?.bestellung_extern_id);
            if (!id || seen.has(id)) {
                continue;
            }
            seen.add(id);
            unique.push(c);
        }
        const current = this.resolvedBestellung;
        if (current && !unique.some(c => Number(c.bestellung_extern_id) === Number(current.bestellung_extern_id))) {
            unique.unshift(current);
        }
        const suggested = Number(this.draft?.suggestedBestellungExternId) || 0;
        return unique.sort((a, b) => {
            const aId = Number(a.bestellung_extern_id);
            const bId = Number(b.bestellung_extern_id);
            if (suggested > 0) {
                if (aId === suggested && bId !== suggested) {
                    return -1;
                }
                if (bId === suggested && aId !== suggested) {
                    return 1;
                }
            }
            const scoreDiff = (this.candidateScores.get(bId) || 0) - (this.candidateScores.get(aId) || 0);
            if (scoreDiff !== 0) {
                return scoreDiff;
            }
            const aTime = new Date((a?.bestellt || a?.erstellt) as string | Date).getTime() || 0;
            const bTime = new Date((b?.bestellt || b?.erstellt) as string | Date).getTime() || 0;
            return bTime - aTime;
        });
    }

    get confirmLabel(): string {
        if (this.phase === 'invoiceReview') {
            return 'Prüfung speichern';
        }
        if (this.selectedKind === 'credit') {
            return 'Gutschrift anlegen';
        }
        if (this.selectedKind === 'invoice') {
            return 'Weiter zur Prüfung';
        }
        return 'Lieferschein hinterlegen';
    }

    get dialogTitle(): string {
        return this.phase === 'invoiceReview' ? 'Rechnung abgleichen' : 'Wareneingang scannen';
    }

    get belegNr(): string {
        return this.draft?.suggestedRechnungNr || '–';
    }

    get belegDatum(): string {
        return this.draft?.suggestedDatum || '–';
    }

    copyScanJson(): void {
        if (!this.isAdmin || !this.draft) {
            return;
        }
        const json = JSON.stringify(this.buildScanDebugJson(), null, 2);
        if (this.clipboard.copy(json)) {
            this.messageService.infoMessage('JSON kopiert – kannst du hier einfügen.');
        } else {
            this.messageService.infoMessage('Kopieren nicht möglich – bitte nochmal versuchen.');
        }
    }

    private buildScanDebugJson(): Record<string, unknown> {
        return {
            file: this.file?.name || this.draft.originalFileName || null,
            runId: this.draft.runId,
            documentKind: this.draft.documentKind || this.selectedKind,
            suggestedLieferantName: this.draft.suggestedLieferantName,
            suggestedLieferantId: this.draft.suggestedLieferantId,
            suggestedRechnungNr: this.draft.suggestedRechnungNr,
            suggestedDatum: this.draft.suggestedDatum,
            suggestedBestellungExternId: this.draft.suggestedBestellungExternId,
            warnings: this.draft.warnings || [],
            extract: this.draft.extract || null,
            positionen: this.draft.positionen || [],
            matches: this.matches,
        };
    }

    get canConfirm(): boolean {
        if (this.scanning || this.saving) {
            return false;
        }
        if (this.phase === 'invoiceReview') {
            return this.rechnungsNummerCtrl.valid && this.rechnungsDatumCtrl.valid && !!this.invoicePruefung;
        }
        return !!this.file && !!this.bestellung;
    }

    ngOnInit() {
        if (this.data?.preferredKind) {
            this.chosenKind = this.data.preferredKind;
            this.selectedKind = this.data.preferredKind;
            this.kindLocked = true;
        }
        this.lieferantCandidates = [...(this.data?.candidates || [])];
        this.resolvedBestellung = this.data?.bestellung || null;
        this.selectedBestellungId = this.resolvedBestellung?.bestellung_extern_id
            ? Number(this.resolvedBestellung.bestellung_extern_id)
            : null;
        const vorhandeneRechnung = this.data?.dokument;
        if (vorhandeneRechnung?.bed_id && this.bestellung) {
            this.selectedKind = 'invoice';
            this.kindLocked = true;
            this.phase = 'invoiceReview';
            this.invoiceDokument = vorhandeneRechnung;
            this.loadExistingInvoicePdf(vorhandeneRechnung);
            this.loadInvoicePruefung(this.bestellung, vorhandeneRechnung);
            return;
        }
        this.initPdfViewer();
        this.startScan();
    }

    compareBestellungId = (a: number | string | null, b: number | string | null): boolean =>
        Number(a) > 0 && Number(a) === Number(b);

    onBestellungSelected(id: number | string) {
        const numId = Number(id);
        if (!(numId > 0)) {
            return;
        }
        const found = this.assignmentOptions.find(c => Number(c.bestellung_extern_id) === numId);
        if (found) {
            this.resolvedBestellung = found;
            this.selectedBestellungId = numId;
            this.matches = this.buildMatches(this.draft);
            // Positionen oft unvollständig in der Listen-Antwort → nachladen
            if (!(found.positionen?.length > 0)) {
                this.loadBestellung(numId, false);
            }
            return;
        }
        this.loadBestellung(numId);
    }

    openBestellungCompare() {
        const id = Number(this.bestellung?.bestellung_extern_id);
        if (!(id > 0)) {
            this.messageService.infoMessage('Bitte zuerst eine Bestellung wählen.');
            return;
        }
        this.dialog.open(BestellungExternDetailsComponent, {
            width: '1100px',
            maxWidth: '96vw',
            height: '90vh',
            autoFocus: false,
            panelClass: ['mat-dialog-no-padding'],
            data: { bestellungExternId: id },
        });
    }

    candidateHint(c: BestellungExtern): string {
        const id = Number(c?.bestellung_extern_id);
        const score = this.candidateScores.get(id) || 0;
        const suggested = Number(this.draft?.suggestedBestellungExternId) || 0;
        const parts: string[] = [];
        if (suggested > 0 && id === suggested) {
            parts.push('Vorschlag');
        }
        if (score > 0) {
            parts.push(`${score} Treffer`);
        }
        return parts.length ? ` (${parts.join(', ')})` : '';
    }

    ngOnDestroy() {
        this.revokePdfUrl();
    }

    selectKind(kind: WareneingangScanKind) {
        this.chosenKind = kind;
        this.selectedKind = kind;
        this.kindLocked = true;
    }

    /** Gewählte Karte schlägt Dateiname und Scan-Vorschlag. */
    private commitKind(): WareneingangScanKind {
        return this.chosenKind || this.selectedKind;
    }

    close() {
        if (this.saving) {
            return;
        }
        this.dialogRef.close();
    }

    onPdfLoad(pdf: { numPages?: number }) {
        this.totalPages = pdf?.numPages ?? 0;
        this.pdfLoading = false;
    }

    onPdfError() {
        this.pdfLoading = false;
    }

    prevPage() {
        if (this.page > 1) {
            this.page--;
        }
    }

    nextPage() {
        if (this.totalPages && this.page < this.totalPages) {
            this.page++;
        }
    }

    confirm() {
        if (this.saving || !this.canConfirm) {
            return;
        }
        if (this.phase === 'invoiceReview') {
            this.saveInvoiceReview();
            return;
        }
        const be = this.bestellung;
        if (this.scanFailed || !this.draft?.runId) {
            if (!be) {
                this.messageService.infoMessage('Bitte zuerst eine Bestellung zuordnen.');
                return;
            }
            const result: InvoiceDeliverNoteManualResult = {
                saved: false,
                kind: this.commitKind(),
                extraFiles: this.extraFiles,
                bestellung: be,
            };
            this.dialogRef.close(result);
            return;
        }

        this.saving = true;
        const kind = this.commitKind();
        this.selectedKind = kind;
        if (kind === 'credit') {
            this.invoiceKi
                .autoCommit(this.draft.runId, {
                    persistTarget: 'gutschrift',
                    bestellungExternId: be?.bestellung_extern_id,
                    documentKind: 'gutschrift',
                })
                .subscribe({
                    next: res => {
                        this.saving = false;
                        this.messageService.infoMessage(
                            res?.gutschriftId
                                ? `Gutschrift angelegt (ID ${res.gutschriftId}) und zugeordnet.`
                                : 'Gutschrift angelegt.',
                        );
                        this.closeSaved();
                    },
                    error: err => this.onCommitError(err),
                });
            return;
        }

        if (!be?.bestellung_extern_id) {
            this.saving = false;
            this.messageService.infoMessage('Bitte eine Bestellung zuordnen.');
            return;
        }

        this.invoiceKi
            .autoCommit(this.draft.runId, {
                persistTarget: 'bestellung_extern',
                bestellungExternId: be.bestellung_extern_id,
                dokumentTyp: kind === 'invoice' ? 2 : 1,
                documentKind: kind === 'invoice' ? 'rechnung' : 'lieferschein',
            })
            .subscribe({
                next: () => {
                    if (kind === 'invoice') {
                        this.enterInvoiceReview(be);
                        return;
                    }
                    this.saving = false;
                    this.messageService.infoMessage(
                        'Lieferschein (PDF) wurde der Bestellung angehängt und hinterlegt.',
                    );
                    this.closeSaved();
                },
                error: err => this.onCommitError(err),
            });
    }

    private enterInvoiceReview(be: BestellungExtern) {
        this.bestellungExternService.getInvoices(be).subscribe({
            next: res => {
                const docs = [...(res?.records || [])].sort(
                    (a, b) => Number(b.bed_id) - Number(a.bed_id),
                );
                const draftNr = (this.draft?.suggestedRechnungNr || '').trim();
                const byNr = draftNr
                    ? docs.find(d => String(d.nummer || '').trim() === draftNr)
                    : null;
                const dokument = byNr || docs[0] || null;
                if (!dokument?.bed_id) {
                    this.saving = false;
                    this.messageService.infoMessage(
                        'Rechnung hinterlegt – Positionsprüfung bitte über die Rechnung in der Bestellung öffnen.',
                    );
                    this.closeSaved();
                    return;
                }
                this.invoiceDokument = dokument;
                this.loadInvoicePruefung(be, dokument);
            },
            error: () => {
                this.saving = false;
                this.messageService.infoMessage(
                    'Rechnung hinterlegt – Positionsprüfung bitte über die Rechnung in der Bestellung öffnen.',
                );
                this.closeSaved();
            },
        });
    }

    private loadInvoicePruefung(be: BestellungExtern, dokument: BestellungExternDokument) {
        let params = new HttpParams()
            .set('bestellung_extern_id', String(be.bestellung_extern_id))
            .set('bed_id', String(dokument.bed_id));
        this.api.get(this.apiBaseUrl + 'bestellung_extern_dokument/read_mit_positionen.php', params).subscribe({
            next: (response: BestellungExternDokumentPruefung) => {
                this.bestellpreisDraft.clear();
                this.summenManuell = false;
                this.invoicePruefung = response;
                this.rechnungsNummerCtrl.setValue(
                    response?.nummer || this.draft?.suggestedRechnungNr || dokument.nummer || null,
                );
                this.rechnungsDatumCtrl.setValue(
                    response?.datum || this.draft?.suggestedDatum || dokument.datum || null,
                );
                this.initInvoicePositionCtrls();
                this.initSummenKontrolle();
                this.loadLieferantGegenkonto();
                this.saving = false;
                this.phase = 'invoiceReview';
            },
            error: err => {
                this.saving = false;
                this.messageService.errorMessage(
                    'Positionen zur Rechnung konnten nicht geladen werden.',
                    err,
                );
                this.closeSaved(dokument);
            },
        });
    }

    private initInvoicePositionCtrls() {
        this.inRechnungCtrls = [];
        this.geprueftCtrls = [];
        const positionen = this.invoicePruefung?.positionen || [];
        for (const p of positionen) {
            if (p.in_rechnung === 1 && Number(p.primanota_id) > 0) {
                this.rechnungsDatumCtrl.disable();
                this.rechnungsNummerCtrl.disable();
            }
        }
        for (let i = 0; i < positionen.length; i++) {
            const p = positionen[i];
            const locked = Number(p.primanota_id) > 0;
            this.inRechnungCtrls[i] = new FormControl({
                value: p.in_rechnung === 0 ? false : true,
                disabled: locked,
            });
            this.geprueftCtrls[i] = new FormControl({
                value: Number(p.geprueft) > 0,
                disabled: locked,
            });
        }
    }

    /** Netto/Brutto aus Scan oder Positionssumme. Folgt dem Bestellpreis, bis die Summen selbst geändert werden. */
    initSummenKontrolle() {
        if (this.summenManuell) {
            return;
        }
        const fromScan = this.extractNetto;
        const fromPos = this.sumNettoInRechnung > 0 ? this.sumNettoInRechnung : null;
        const netto = fromScan ?? fromPos;
        if (netto != null && netto > 0) {
            this.kontrollNetto = Math.round(netto * 100) / 100;
            const brutto = this.extractBrutto ?? this.suggestBruttoFromNetto(this.kontrollNetto);
            this.kontrollBrutto = brutto != null && brutto > 0 ? Math.round(brutto * 100) / 100 : null;
        } else if (fromScan == null) {
            this.kontrollNetto = null;
            this.kontrollBrutto = null;
        }
        this.syncKontrollMwstFromNettoBrutto();
    }

    private syncKontrollMwstFromNettoBrutto() {
        if (this.kontrollNetto != null && this.kontrollBrutto != null) {
            const mwst = Math.round((Number(this.kontrollBrutto) - Number(this.kontrollNetto)) * 100) / 100;
            this.kontrollMwst = mwst >= 0 ? mwst : null;
        } else {
            this.kontrollMwst = null;
        }
    }

    private suggestBruttoFromNetto(netto: number | null): number | null {
        if (netto == null || !(netto > 0)) {
            return null;
        }
        const mwst =
            this.draft?.extract?.mwstSatz ??
            this.draft?.preise?.[0]?.mwstsatz ??
            19;
        if (!(Number(mwst) > 0)) {
            return null;
        }
        return Math.round(netto * (1 + Number(mwst) / 100) * 100) / 100;
    }

    private loadLieferantGegenkonto() {
        const lid = Number(this.bestellung?.lieferant_id) || 0;
        if (!(lid > 0)) {
            this.lieferantGegenkonto = null;
            this.lieferantKreditor = null;
            return;
        }
        this.api
            .get(this.apiBaseUrl + 'lieferant/read_one.php', new HttpParams().set('lieferant_id', String(lid)))
            .subscribe({
                next: (rec: { buchungsgegenkonto?: string; buchungskonto?: string }) => {
                    this.lieferantGegenkonto = (rec?.buchungsgegenkonto || '').trim() || null;
                    this.lieferantKreditor = (rec?.buchungskonto || '').trim() || null;
                },
                error: () => {
                    this.lieferantGegenkonto = null;
                    this.lieferantKreditor = null;
                },
            });
    }

    isVerbrauchPos(p: BestellungExternPosition): boolean {
        return isEinkaufVerbrauchArticle(p as any, Number(p.laborartikel) > 0);
    }

    predictedKonto(p: BestellungExternPosition): string {
        return einkaufAufwandskonto(p as any, this.lieferantGegenkonto);
    }

    canEditStammFibu(p: BestellungExternPosition): boolean {
        return Number(p.primanota_id) <= 0 && Number(p.artikel_id) > 0 && this.stammSavingBeaId == null;
    }

    setVerbrauchPos(p: BestellungExternPosition, verbrauch: boolean) {
        if (!this.canEditStammFibu(p)) {
            return;
        }
        if (this.isVerbrauchPos(p) === verbrauch) {
            return;
        }
        const labor = Number(p.laborartikel) > 0;
        // Ausnahme-Stammkonto (≠ 3400) bei Toggle behalten
        const stamm = (p.buchungskonto || '').trim();
        const keepAusnahme =
            !!stamm &&
            stamm !== '3400' &&
            !stamm.startsWith('3400.') &&
            stamm !== '4981' &&
            stamm !== '4982';
        // Bis 1.1.2027: nur Verbrauch-Flag, Konto bleibt 3400 (keine 4981/4982 am Stamm).
        let nextKonto = '3400';
        if (keepAusnahme) {
            nextKonto = stamm;
        } else if (isFibuEinkaufArtikelAbAktiv()) {
            nextKonto = verbrauch ? (labor ? '4982' : '4981') : '3400';
        }
        this.saveArtikelEinkaufFibu(p, {
            verbrauch: verbrauch ? 1 : 0,
            buchungskonto: nextKonto,
        });
    }

    editArtikelKonto(p: BestellungExternPosition) {
        if (!this.canEditStammFibu(p)) {
            return;
        }
        const input = new EnterValueInput();
        input.title = 'Aufwandskonto Artikelstamm';
        input.addInputValue('Konto', 'text', this.predictedKonto(p));
        this.dialog
            .open(EnterValueDialogComponent, { data: input, width: '360px' })
            .afterClosed()
            .subscribe({
                next: (result: EnterValueResponse) => {
                    if (!result?.result) {
                        return;
                    }
                    const raw = String(result.getValue(0) ?? '')
                        .trim()
                        .replace(/\s/g, '');
                    if (!raw) {
                        this.messageService.infoMessage('Konto darf nicht leer sein.');
                        return;
                    }
                    const payload: { verbrauch?: number; buchungskonto: string } = {
                        buchungskonto: raw,
                    };
                    // Klare Einkaufskonten → Verbrauch-Flag mitziehen
                    if (raw === '4981' || raw === '4982' || raw.startsWith('4981') || raw.startsWith('4982')) {
                        payload.verbrauch = 1;
                    } else if (raw === '3400' || raw.startsWith('3400.')) {
                        payload.verbrauch = 0;
                    }
                    this.saveArtikelEinkaufFibu(p, payload);
                },
            });
    }

    private saveArtikelEinkaufFibu(
        p: BestellungExternPosition,
        payload: { verbrauch?: number; buchungskonto?: string },
    ) {
        this.stammSavingBeaId = Number(p.bea_id);
        this.api
            .updateGeneric(this.apiBaseUrl + 'artikel/update_einkauf_fibu.php', {
                artikel_id: p.artikel_id,
                ...payload,
            })
            .subscribe({
                next: (rec: {
                    artikel_id?: number;
                    verbrauch?: number;
                    buchungskonto?: string;
                    laborartikel?: number;
                    nicht_abgebbar?: number;
                }) => {
                    if (payload.verbrauch != null) {
                        p.verbrauch = payload.verbrauch;
                    } else if (rec?.verbrauch != null) {
                        p.verbrauch = Number(rec.verbrauch);
                    }
                    if (payload.buchungskonto != null) {
                        p.buchungskonto = payload.buchungskonto;
                    } else if (rec?.buchungskonto != null) {
                        p.buchungskonto = rec.buchungskonto;
                    }
                    this.stammSavingBeaId = null;
                    this.messageService.infoMessage(
                        `Artikelstamm aktualisiert · ${this.isVerbrauchPos(p) ? 'Verbrauch' : 'Umsatz'} · Konto ${this.predictedKonto(p)}`,
                    );
                },
                error: err => {
                    this.stammSavingBeaId = null;
                    this.messageService.errorMessage(
                        'Artikelstamm (Verbrauch/Konto) konnte nicht gespeichert werden.',
                        err,
                    );
                },
            });
    }

    alleAnhakenInRechnung(event: MatCheckboxChange) {
        if (!event.checked) {
            return;
        }
        for (const ctrl of this.inRechnungCtrls) {
            if (ctrl.enabled) {
                ctrl.setValue(true);
            }
        }
    }

    /** Rohtext, solange das Feld fokussiert ist – sonst formatierte Anzeige. */
    private bestellpreisDraft = new Map<number, string>();

    bestellpreisText(p: BestellungExternPosition): string {
        const id = Number(p.bea_id);
        if (this.bestellpreisDraft.has(id)) {
            return this.bestellpreisDraft.get(id) ?? '';
        }
        if (p.einzelpreis == null || String(p.einzelpreis).trim() === '') {
            return '';
        }
        return this.bestellPreisAnzeige(p);
    }

    focusBestellpreis(p: BestellungExternPosition) {
        if (Number(p.primanota_id) > 0) {
            return;
        }
        const id = Number(p.bea_id);
        if (!this.bestellpreisDraft.has(id)) {
            this.bestellpreisDraft.set(id, this.bestellpreisText(p));
        }
    }

    onBestellpreisChange(p: BestellungExternPosition, raw: string | number) {
        if (Number(p.primanota_id) > 0) {
            return;
        }
        const text = raw == null ? '' : String(raw);
        this.bestellpreisDraft.set(Number(p.bea_id), text);
        this.applyBestellpreisText(p, text, false);
    }

    blurBestellpreis(p: BestellungExternPosition) {
        const id = Number(p.bea_id);
        const text = this.bestellpreisDraft.get(id);
        this.bestellpreisDraft.delete(id);
        if (text != null && Number(p.primanota_id) <= 0) {
            this.applyBestellpreisText(p, text, true);
        }
    }

    private applyBestellpreisText(p: BestellungExternPosition, raw: string, commit: boolean) {
        const trimmed = raw.trim();
        if (trimmed === '' || trimmed === '–') {
            p.einzelpreis = null;
            p.preis_abweichend = this.isPreisAbweichend(p) ? 1 : 0;
            this.initSummenKontrolle();
            return;
        }
        if (!commit && /[.,]$/.test(trimmed)) {
            return;
        }
        const parsed = this.parseDecimal(trimmed);
        if (parsed == null) {
            return;
        }
        p.einzelpreis = Math.round(parsed * 100) / 100;
        p.preis_abweichend = this.isPreisAbweichend(p) ? 1 : 0;
        this.initSummenKontrolle();
    }

    private parseDecimal(raw: string): number | null {
        let s = raw.trim().replace(/\s/g, '').replace('€', '');
        if (s.includes(',')) {
            s = s.replace(/\./g, '').replace(',', '.');
        }
        const n = Number(s);
        return Number.isFinite(n) ? n : null;
    }

    /** Nur echter Scan-NEP – kein Bestellpreis als Fake-Beleg. */
    belegPreisAnzeige(p: BestellungExternPosition): string {
        const ocr = this.ocrNep(p);
        if (ocr == null) {
            return '';
        }
        return (Math.round(ocr * 100) / 100).toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    }

    bestellPreisAnzeige(p: BestellungExternPosition): string {
        if (p.einzelpreis == null) {
            return '–';
        }
        return (Math.round(Number(p.einzelpreis) * 100) / 100).toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    }

    /** Scan-NEP weicht vom Bestellpreis ab → Übernehmen anbieten. */
    canApplyBelegPreis(p: BestellungExternPosition): boolean {
        if (Number(p.primanota_id) > 0) {
            return false;
        }
        const ocr = this.ocrNep(p);
        if (ocr == null || !(ocr > 0)) {
            return false;
        }
        return Math.abs(this.positionNep(p) - ocr) > 0.02;
    }

    /** Beleg-NEP in die Bestellposition übernehmen (nicht Artikelstamm). */
    applyBelegPreis(p: BestellungExternPosition) {
        if (Number(p.primanota_id) > 0) {
            return;
        }
        const nep = this.ocrNep(p);
        if (nep == null || !(nep > 0)) {
            this.messageService.infoMessage('Kein Belegpreis erkannt.');
            return;
        }
        // Kaufmännisch auf Cent – gleiche Anzeige wie Stamm/Beleg-Felder
        p.einzelpreis = Math.round(nep * 100) / 100;
        p.rabatt = 0;
        p.preis_abweichend = this.isPreisAbweichend(p) ? 1 : 0;
        this.bestellpreisDraft.delete(Number(p.bea_id));
        this.initSummenKontrolle();
    }

    isPreisAbweichend(p: BestellungExternPosition): boolean {
        if (this.bestellung?.untersuchungsauftrag_id) {
            return false;
        }
        if (Number(p.preis_abweichend) > 0) {
            return true;
        }
        const lieferant = p.einzelpreis_lieferant;
        if (lieferant == null || p.einzelpreis == null) {
            return false;
        }
        return Math.abs(Number(p.einzelpreis) - Number(lieferant)) > 0.00001;
    }

    /** Netto-Einzelpreis nach Rabatt (System). */
    positionNep(p: BestellungExternPosition): number {
        const ep = Number(p.einzelpreis) || 0;
        const rabatt = Number(p.rabatt) || 0;
        if (rabatt > 0) {
            return Math.round(ep * (100 - rabatt) * 100) / 10000;
        }
        return Math.round(ep * 100000) / 100000;
    }

    positionZeilenNetto(p: BestellungExternPosition): number {
        const qty = Number(p.menge_soll) || 0;
        return Math.round(this.positionNep(p) * qty * 100) / 100;
    }

    private ocrLineForPosition(p: BestellungExternPosition): {
        menge: number | null;
        netto: number | null;
        bezeichnung?: string;
    } | null {
        const lines = this.ocrLines(this.draft).filter(
            l => (l.netto != null && l.netto > 0) || (l.menge != null && l.menge > 0),
        );
        const byId = lines.find(l => l.artikelId && Number(l.artikelId) === Number(p.artikel_id));
        if (byId) {
            return byId;
        }
        const name = (p.artikel || p.artikel_me || '').trim().toLowerCase();
        if (name) {
            const tokens = name
                .replace(/[^a-z0-9äöüß]+/gi, ' ')
                .split(/\s+/)
                .filter(t => t.length >= 4)
                .slice(0, 4);
            const byName = lines.find(l => {
                const n = (l.bezeichnung || '').trim().toLowerCase();
                if (!n) {
                    return false;
                }
                if (n.includes(name.slice(0, 16)) || name.includes(n.slice(0, 16))) {
                    return true;
                }
                return tokens.some(t => n.includes(t));
            });
            // Zeile mit Netto bevorzugen, falls Namen mehrfach treffen
            if (byName?.netto != null && byName.netto > 0) {
                return byName;
            }
            const byNameWithNetto = lines.find(l => {
                const n = (l.bezeichnung || '').trim().toLowerCase();
                if (!n || !(l.netto != null && l.netto > 0)) {
                    return false;
                }
                return tokens.some(t => n.includes(t)) || n.includes(name.slice(0, 12));
            });
            if (byNameWithNetto) {
                return byNameWithNetto;
            }
            if (byName) {
                return byName;
            }
        }
        const qty = Number(p.menge_soll) || 0;
        if (qty > 0) {
            const byQtyNetto = lines
                .filter(l => Number(l.menge) === qty && l.netto != null && l.netto > 0)
                .sort((a, b) => Number(b.netto) - Number(a.netto));
            if (byQtyNetto.length >= 1) {
                return byQtyNetto[0];
            }
            const byQty = lines.filter(l => Number(l.menge) === qty);
            if (byQty.length === 1) {
                return byQty[0];
            }
        }
        // Eine Warenposition + eine OCR-Zeile mit Netto
        const withNetto = lines.filter(l => l.netto != null && l.netto > 0);
        if (withNetto.length === 1) {
            return withNetto[0];
        }
        return null;
    }

    /** OCR → Einzelpreis (NEP) auf dem Beleg – Zeilensumme und Textract-Doppel×Menge bereinigen. */
    ocrNep(p: BestellungExternPosition): number | null {
        const systemNep = this.positionNep(p);
        const line = this.ocrLineForPosition(p);
        const qty =
            (line?.menge != null && line.menge > 0 ? Number(line.menge) : 0) ||
            Number(p.menge_soll) ||
            0;
        let amount: number | null =
            line?.netto != null && line.netto > 0 ? Number(line.netto) : null;

        if (amount == null) {
            const extract = this.rawExtractNetto();
            if (extract != null && extract > 0 && qty > 0) {
                const inReCount = (this.inRechnungCtrls || []).filter(c => c?.value).length;
                const posCount = this.invoicePruefung?.positionen?.length || 0;
                if (inReCount <= 1 || posCount <= 1) {
                    amount = extract;
                }
            }
        }
        if (amount == null || !(amount > 0)) {
            return null;
        }

        if (qty > 1 && systemNep > 0) {
            const zeile = Math.round(systemNep * qty * 100) / 100;
            const doppelt = Math.round(zeile * qty * 100) / 100;
            const tol = (v: number) => Math.max(0.05, Math.abs(v) * 0.001);
            // Bereits Einzelpreis
            if (Math.abs(amount - systemNep) <= 0.05) {
                return Math.round(systemNep * 100) / 100;
            }
            // Zeilensumme (120 × 12,95 = 1.554)
            if (Math.abs(amount - zeile) <= tol(zeile)) {
                return Math.round(systemNep * 100) / 100;
            }
            // Textract: Zeilensumme×Menge (1.554 × 120 = 186.480)
            if (Math.abs(amount - doppelt) <= tol(doppelt)) {
                return Math.round(systemNep * 100) / 100;
            }
        }

        // Ohne Systempreis: absurde Beträge einmal / zweimal durch Menge
        if (qty >= 20 && amount >= 50_000) {
            amount = Math.round((amount / qty) * 100) / 100;
        }
        if (qty > 1 && amount > qty * 0.5) {
            const unit = Math.round((amount / qty) * 100) / 100;
            // Noch Zeilensumme? (z. B. 1.554 bei Menge 120)
            if (qty >= 20 && unit >= 100 && unit / qty >= 0.01 && unit > qty * 0.5) {
                return Math.round((unit / qty) * 100) / 100;
            }
            return unit;
        }

        return Math.round(amount * 100) / 100;
    }

    /** Implizierter Rabatt: Stamm/Listenpreis → Beleg-NEP (falls OCR oder Stamm bekannt). */
    impliedRabattPct(p: BestellungExternPosition): number | null {
        const listen = Number(p.einzelpreis_lieferant) || Number(p.einzelpreis) || 0;
        const nep = this.ocrNep(p);
        if (!(listen > 0) || nep == null || !(nep > 0) || nep >= listen) {
            return null;
        }
        return Math.round(((1 - nep / listen) * 100) * 100) / 100;
    }

    priceCheck(p: BestellungExternPosition): {
        status: 'ok' | 'warn' | 'unknown';
        label: string;
        systemNep: number;
        ocrNep: number | null;
        rabattPct: number | null;
    } {
        const systemNep = this.positionNep(p);
        const ocrNep = this.ocrNep(p);
        const rabattPct = this.impliedRabattPct(p) ?? (Number(p.rabatt) > 0 ? Number(p.rabatt) : null);
        if (ocrNep == null) {
            // Kein Scan-Einzelpreis – keine Alarm-Meldung (Feld zeigt Bestellpreis)
            return {
                status: 'unknown',
                label: '',
                systemNep,
                ocrNep,
                rabattPct,
            };
        }
        const diff = Math.abs(systemNep - ocrNep);
        if (diff <= 0.02) {
            return {
                status: 'ok',
                label: '',
                systemNep,
                ocrNep,
                rabattPct,
            };
        }
        return {
            status: 'warn',
            label: `Einzelpreis Beleg ${ocrNep.toFixed(2)} € ≠ Bestellung ${systemNep.toFixed(2)} €`,
            systemNep,
            ocrNep,
            rabattPct,
        };
    }

    get extractNetto(): number | null {
        const raw = this.rawExtractNetto();
        return this.sanitizeScanNetto(raw);
    }

    /** Roh-Netto aus Scan (ohne Korrektur). */
    private rawExtractNetto(): number | null {
        const ex = this.draft?.extract as Record<string, unknown> | undefined;
        const fromExtract =
            this.draft?.extract?.nettoGesamt ??
            (ex?.['netto_gesamt'] != null ? Number(ex['netto_gesamt']) : null);
        if (fromExtract != null && Number(fromExtract) > 0) {
            return Number(fromExtract);
        }
        const preise = this.draft?.preise ?? [];
        let sumPreise = 0;
        let anyPreis = false;
        for (const p of preise) {
            const n = Number(p?.kostenNetto ?? p?.kosten_netto ?? 0);
            if (n > 0) {
                anyPreis = true;
                sumPreise += n;
            }
        }
        if (anyPreis) {
            return Math.round(sumPreise * 100) / 100;
        }
        const lines = this.ocrLines(this.draft).filter(
            l => l.netto != null && l.netto > 0 && l.menge != null && l.menge > 0,
        );
        if (lines.length === 1) {
            return Math.round(Number(lines[0].netto) * 100) / 100;
        }
        if (lines.length > 1) {
            const sum = lines.reduce((s, l) => s + Number(l.netto), 0);
            return Math.round(sum * 100) / 100;
        }
        const onlyNetto = this.ocrLines(this.draft).filter(l => l.netto != null && l.netto > 0);
        if (onlyNetto.length === 1) {
            return Math.round(Number(onlyNetto[0].netto) * 100) / 100;
        }
        return null;
    }

    /**
     * Veyx u.ä.: Scan-Netto = Zeilensumme×Menge (186.480 statt 1.554).
     * Mit Positions-Summe / OCR-Menge zurückrechnen.
     */
    private sanitizeScanNetto(raw: number | null): number | null {
        if (raw == null || !(raw > 0)) {
            return null;
        }
        const posSum = this.sumNettoInRechnung;
        if (posSum > 0 && raw > posSum * 5) {
            const qty =
                this.ocrLines(this.draft).find(l => l.menge != null && l.menge >= 20)?.menge ??
                this.invoicePruefung?.positionen?.find(p => Number(p.menge_soll) >= 20)?.menge_soll ??
                null;
            if (qty != null && Number(qty) >= 20) {
                const undoubled = Math.round((raw / Number(qty)) * 100) / 100;
                if (
                    undoubled >= 50 &&
                    Math.abs(undoubled - posSum) <= Math.max(0.5, posSum * 0.02)
                ) {
                    return posSum;
                }
                if (
                    undoubled >= 100 &&
                    undoubled < 50_000 &&
                    Math.abs(undoubled * Number(qty) - raw) <= Math.max(1, raw * 0.001)
                ) {
                    return undoubled;
                }
            }
            // Ohne Menge: Positions-Summe als Scan-Netto bevorzugen, wenn Scan absurd hoch
            if (raw > posSum * 20) {
                return posSum;
            }
        }
        return Math.round(raw * 100) / 100;
    }

    get summenAbweichung(): { status: 'ok' | 'warn' | 'idle'; label: string } {
        const extract = this.extractNetto;
        const sum = this.sumNettoInRechnung;
        if (extract == null || !(sum > 0)) {
            return { status: 'idle', label: '' };
        }
        const diff = Math.round((sum - extract) * 100) / 100;
        // Bis ~10 Cent: typische Cent-Rundung (NEP × Menge vs. Beleg-Gesamt)
        if (Math.abs(diff) <= 0.1) {
            return { status: 'ok', label: 'Positionssumme ≈ Scan-Netto' };
        }
        return {
            status: 'warn',
            label: `Positionssumme ${sum.toFixed(2)} € ≠ Scan ${extract.toFixed(2)} € (Diff ${diff > 0 ? '+' : ''}${diff.toFixed(2)} €)`,
        };
    }

    get extractBrutto(): number | null {
        const ex = this.draft?.extract as Record<string, unknown> | undefined;
        const n =
            this.draft?.extract?.bruttoGesamt ??
            (ex?.['brutto_gesamt'] != null ? Number(ex['brutto_gesamt']) : null);
        return n != null && Number(n) > 0 ? Number(n) : null;
    }

    /** Summe Netto der „in Rechnung“-Positionen. */
    get sumNettoInRechnung(): number {
        const pos = this.invoicePruefung?.positionen || [];
        let sum = 0;
        for (let i = 0; i < pos.length; i++) {
            if (this.inRechnungCtrls[i]?.value) {
                sum += this.positionZeilenNetto(pos[i]);
            }
        }
        return Math.round(sum * 100) / 100;
    }

    get kontrollNettoStatus(): 'ok' | 'warn' | 'idle' {
        if (this.kontrollNetto == null || !(Number(this.kontrollNetto) > 0)) {
            return 'idle';
        }
        const target = this.extractNetto ?? this.sumNettoInRechnung;
        return Math.abs(Number(this.kontrollNetto) - target) <= 0.02 ? 'ok' : 'warn';
    }

    get kontrollMwstStatus(): 'ok' | 'warn' | 'idle' {
        if (
            this.kontrollMwst == null ||
            this.kontrollNetto == null ||
            this.kontrollBrutto == null ||
            !(Number(this.kontrollNetto) > 0) ||
            !(Number(this.kontrollBrutto) > 0)
        ) {
            return 'idle';
        }
        const expected = Math.round((Number(this.kontrollBrutto) - Number(this.kontrollNetto)) * 100) / 100;
        return Math.abs(Number(this.kontrollMwst) - expected) <= 0.02 ? 'ok' : 'warn';
    }

    get kontrollBruttoStatus(): 'ok' | 'warn' | 'idle' {
        if (this.kontrollBrutto == null || !(Number(this.kontrollBrutto) > 0)) {
            return 'idle';
        }
        const target =
            this.extractBrutto ?? this.suggestBruttoFromNetto(this.extractNetto ?? this.sumNettoInRechnung);
        if (target == null) {
            return 'idle';
        }
        return Math.abs(Number(this.kontrollBrutto) - target) <= 0.02 ? 'ok' : 'warn';
    }

    parseKontrollNumber(raw: string | number, which: 'netto' | 'mwst' | 'brutto') {
        this.summenManuell = true;
        let n: number | null = null;
        if (typeof raw === 'number' && !Number.isNaN(raw)) {
            n = raw;
        } else if (raw != null && String(raw).trim() !== '') {
            const s = String(raw).trim().replace(/\s/g, '').replace(',', '.');
            const parsed = Number(s);
            n = Number.isFinite(parsed) ? parsed : null;
        }
        if (which === 'netto') {
            this.kontrollNetto = n;
            if (n != null && this.kontrollMwst != null) {
                this.kontrollBrutto = Math.round((n + Number(this.kontrollMwst)) * 100) / 100;
            } else {
                this.syncKontrollMwstFromNettoBrutto();
            }
        } else if (which === 'mwst') {
            this.kontrollMwst = n;
            if (n != null && this.kontrollNetto != null) {
                this.kontrollBrutto = Math.round((Number(this.kontrollNetto) + n) * 100) / 100;
            }
        } else {
            this.kontrollBrutto = n;
            if (n != null && this.kontrollNetto != null) {
                this.kontrollMwst = Math.round((n - Number(this.kontrollNetto)) * 100) / 100;
            }
        }
    }

    togglePreisAbweichend(p: BestellungExternPosition) {
        if (Number(p.primanota_id) > 0) {
            return;
        }
        p.preis_abweichend = Number(p.preis_abweichend) > 0 ? 0 : 1;
    }

    /** Wie betriebliche Kosten: Vorschau aus aktueller Eingabe – ohne Primanota/API. */
    openPrimanotaExportVorschau() {
        if (!this.invoicePruefung?.positionen?.length) {
            this.messageService.infoMessage('Keine Positionen geladen.');
            return;
        }
        this.dialog.open(NebenkostenPrimanotaVorschauDialogComponent, {
            width: '920px',
            maxWidth: '96vw',
            data: this.buildPrimanotaExportVorschau(),
        });
    }

    private buildPrimanotaExportVorschau(): PrimanotaExportVorschauData {
        return {
            neu: this.buildPrimanotaVorschauNeu(),
            alt: this.buildPrimanotaVorschauAlt(),
            quelle: 'aktuelle Eingabe im Review',
        };
    }

    private positionenInRechnung(): BestellungExternPosition[] {
        const pos = this.invoicePruefung?.positionen || [];
        const out: BestellungExternPosition[] = [];
        for (let i = 0; i < pos.length; i++) {
            if (this.inRechnungCtrls[i]?.value) {
                out.push(pos[i]);
            }
        }
        return out.length ? out : pos;
    }

    /** Für Primanota-Vorschau: freigegebene Positionen, sonst „In Rechnung“. */
    private positionenFuerPrimanotaVorschau(): BestellungExternPosition[] {
        const pos = this.invoicePruefung?.positionen || [];
        const freigegeben: BestellungExternPosition[] = [];
        const inRechnung: BestellungExternPosition[] = [];
        for (let i = 0; i < pos.length; i++) {
            if (this.geprueftCtrls[i]?.value) {
                freigegeben.push(pos[i]);
            }
            if (this.inRechnungCtrls[i]?.value) {
                inRechnung.push(pos[i]);
            }
        }
        return freigegeben.length ? freigegeben : inRechnung.length ? inRechnung : pos;
    }

    private previewMwstSatz(): number {
        const fromDraft =
            this.draft?.extract?.mwstSatz ?? this.draft?.preise?.[0]?.mwstsatz ?? null;
        const n = Number(fromDraft);
        return n > 0 ? n : 19;
    }

    private buildPrimanotaVorschauNeu(): PrimanotaExportVorschauVariante {
        const hinweise: string[] = [];
        const gegenkonto = (this.lieferantKreditor || '').trim();
        if (!gegenkonto) {
            hinweise.push('Kein Kreditor-Konto am Lieferanten – Gegenkonto fehlt.');
        }
        if (!(Number(this.bestellung?.lieferant_id) > 0)) {
            hinweise.push('Kein Lieferant an der Bestellung.');
        }

        const beleg = (this.rechnungsNummerCtrl.value || '').trim();
        const belegdatum = this.formatPrimanotaBelegdatum(this.rechnungsDatumCtrl.value);
        const kopfText = this.buildPrimanotaBuchungstext();
        const mwst = this.previewMwstSatz();

        type Acc = {
            konto: string;
            sollhaben: 'S' | 'H';
            bu: number | null;
            umsatz: number;
            texte: string[];
        };
        const byKey = new Map<string, Acc>();

        for (const p of this.positionenFuerPrimanotaVorschau()) {
            // Vorschau „Neu“ zeigt Artikelkonten auch vor dem Stichtag 1.1.2027
            const konto = (
                einkaufAufwandskonto(p as any, this.lieferantGegenkonto, {
                    artikelKonten: true,
                }) || ''
            ).trim();
            const teil = this.positionBuchungstextTeil(p) || kopfText;
            if (!konto) {
                hinweise.push(`Position ohne Aufwandskonto: ${teil || '–'}`);
                continue;
            }
            const netto = Math.abs(this.positionZeilenNetto(p));
            const brutto = Math.round((netto + (netto * mwst) / 100) * 100) / 100;
            if (brutto <= 0) {
                hinweise.push(`Position ohne Betrag: ${teil || '–'}`);
                continue;
            }
            const bu = this.buSchluesselFromMwst(mwst);
            const key = `S|${konto}|${bu ?? ''}`;
            const prev = byKey.get(key);
            if (prev) {
                prev.umsatz = Math.round((prev.umsatz + brutto) * 100) / 100;
                if (teil && !prev.texte.includes(teil)) {
                    prev.texte.push(teil);
                }
            } else {
                byKey.set(key, {
                    konto,
                    sollhaben: 'S',
                    bu,
                    umsatz: brutto,
                    texte: teil ? [teil] : [],
                });
            }
        }

        const zeilen: PrimanotaExportVorschauZeile[] = Array.from(byKey.values()).map(a => ({
            sollhaben: a.sollhaben,
            umsatz: a.umsatz,
            konto: a.konto,
            gegenkonto,
            bu_schluessel: a.bu,
            belegdatum,
            beleg,
            buchungstext: this.formatBestellBuchungstext(a.texte),
        }));

        if (!zeilen.length && !hinweise.length) {
            hinweise.push('Keine Positionen mit Betrag vorhanden.');
        }

        return {
            zeilen,
            hinweise: [...new Set(hinweise)],
            beschreibung:
                'Neu (Journal): Aufwandskonto je Position (3400 / 4981 / 4982 …) an Kreditor, Brutto mit BU.',
        };
    }

    private buildPrimanotaVorschauAlt(): PrimanotaExportVorschauVariante {
        const hinweise: string[] = [];
        const konto = (this.lieferantGegenkonto || '').trim();
        const gegenkonto = (this.lieferantKreditor || '').trim();
        if (!konto) {
            hinweise.push(
                'Kein Aufwand-Gegenkonto am Lieferanten (buchungsgegenkonto) – Legacy-Konto fehlt.',
            );
        }
        if (!gegenkonto) {
            hinweise.push('Kein Kreditor-Konto am Lieferanten (buchungskonto).');
        }

        const beleg = (this.rechnungsNummerCtrl.value || '').trim();
        const belegdatum = this.formatPrimanotaBelegdatum(this.rechnungsDatumCtrl.value);
        const mwst = this.previewMwstSatz();

        type Acc = { mwst: number; sollhaben: 'S' | 'H'; umsatz: number; texte: string[] };
        const byKey = new Map<string, Acc>();

        for (const p of this.positionenFuerPrimanotaVorschau()) {
            const netto = Math.abs(this.positionZeilenNetto(p));
            const brutto = Math.round((netto + (netto * mwst) / 100) * 100) / 100;
            if (brutto <= 0) {
                continue;
            }
            const teil = this.positionBuchungstextTeil(p);
            const key = `S|${Math.round(mwst)}`;
            const prev = byKey.get(key);
            if (prev) {
                prev.umsatz = Math.round((prev.umsatz + brutto) * 100) / 100;
                if (teil && !prev.texte.includes(teil)) {
                    prev.texte.push(teil);
                }
            } else {
                byKey.set(key, {
                    mwst: Math.round(mwst),
                    sollhaben: 'S',
                    umsatz: brutto,
                    texte: teil ? [teil] : [],
                });
            }
        }

        const zeilen: PrimanotaExportVorschauZeile[] = Array.from(byKey.values()).map(a => ({
            sollhaben: a.sollhaben,
            umsatz: a.umsatz,
            konto,
            gegenkonto,
            bu_schluessel: this.buSchluesselLegacyFromMwst(a.mwst),
            belegdatum,
            beleg,
            buchungstext: this.formatBestellBuchungstext(a.texte),
        }));

        if (!zeilen.length && !hinweise.length) {
            hinweise.push('Keine Positionen mit Betrag vorhanden.');
        }

        return {
            zeilen,
            hinweise: [...new Set(hinweise)],
            beschreibung:
                'Alt (Legacy): ein Aufwandskonto vom Lieferanten (Gegenkonto), gruppiert je MwSt.',
        };
    }

    private buSchluesselFromMwst(mwst: number): number | null {
        const m = Math.round(Number(mwst) || 0);
        if (m === 19) {
            return 9;
        }
        if (m === 7) {
            return 8;
        }
        return null;
    }

    private buSchluesselLegacyFromMwst(mwst: number): number | null {
        const m = Math.round(Number(mwst) || 0);
        if (m === 19) {
            return 9;
        }
        if (m === 7 || m === 9) {
            return 8;
        }
        if (m === 0) {
            return 0;
        }
        return null;
    }

    private formatPrimanotaBelegdatum(raw: unknown): string {
        if (!raw) {
            return '';
        }
        const d = raw instanceof Date ? raw : new Date(raw as string);
        if (Number.isNaN(d.getTime())) {
            return '';
        }
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yy = String(d.getFullYear()).slice(-2);
        return `${dd}${mm}${yy}`;
    }

    private buildPrimanotaBuchungstext(): string {
        const nr = (this.rechnungsNummerCtrl.value || '').trim();
        const lief = (this.bestellung?.lieferant || '').trim();
        const first = this.positionenInRechnung()[0];
        const art = (first?.artikel_me || first?.artikel || '').trim();
        return `${nr} ${lief || art}`.trim();
    }

    /** Artikel + optional Charge + Menge für den Buchungstext. */
    private positionBuchungstextTeil(p: BestellungExternPosition): string {
        const art = (p.artikel_me || p.artikel || '').trim();
        const parts: string[] = [];
        if (art) {
            parts.push(art);
        }
        const charge = (p.chargen || '').trim();
        if (charge) {
            // API liefert oft schon „2x LOT123“
            parts.push(/\d+x\s/i.test(charge) ? charge : `Ch. ${charge}`);
        }
        const qty = Number(p.menge_ist) > 0 ? Number(p.menge_ist) : Number(p.menge_soll) || 0;
        if (qty > 0) {
            const unit = (p.einheit || '').trim() || 'Stk';
            const qtyStr = Number.isInteger(qty) ? String(qty) : String(Math.round(qty * 1000) / 1000);
            parts.push(`${qtyStr} ${unit}`);
        }
        return parts.join('; ');
    }

    /** Wie Primanota-Generierung: `#Bestellnr; Artikel; Ch. …; Menge`. */
    private formatBestellBuchungstext(teile: string[]): string {
        const nr =
            this.bestellung?.bestell_nr ??
            this.positionenInRechnung()[0]?.bestell_nr ??
            null;
        const body = teile.filter(t => !!(t || '').trim()).join(', ');
        const base =
            nr != null && String(nr).trim() !== ''
                ? `#${nr}${body ? '; ' + body : ''}`
                : body;
        return base.substring(0, 250);
    }

    private saveInvoiceReview() {
        if (!this.invoicePruefung || !this.canConfirm) {
            return;
        }
        const input = new ConfirmDialogInput();
        input.headline = 'Speichern';
        input.text = 'Rechnungszuordnung und Freigaben speichern?';
        input.okButton = 'Speichern';
        this.dialog
            .open(ConfirmDialogComponent, { width: '25%', data: input })
            .afterClosed()
            .subscribe({
                next: (result: ConfirmDialogResult) => {
                    if (!result?.confirmed) {
                        return;
                    }
                    this.saving = true;
                    for (let i = 0; i < this.invoicePruefung.positionen.length; i++) {
                        this.invoicePruefung.positionen[i].in_rechnung = this.inRechnungCtrls[i]
                            .value
                            ? 1
                            : 0;
                        this.invoicePruefung.positionen[i].geprueft = this.geprueftCtrls[i].value
                            ? 1
                            : 0;
                    }
                    this.invoicePruefung.nummer = this.rechnungsNummerCtrl.value;
                    this.invoicePruefung.datum = this.rechnungsDatumCtrl.value as any;
                    this.api
                        .updateGeneric(
                            this.apiBaseUrl + 'bestellung_extern/update_rechnung.php',
                            this.invoicePruefung,
                        )
                        .subscribe({
                            next: () => {
                                this.saving = false;
                                this.messageService.infoMessage('Rechnung geprüft und gespeichert.');
                                this.closeSaved(this.invoiceDokument);
                            },
                            error: err => {
                                this.saving = false;
                                this.messageService.errorMessage('Speichern fehlgeschlagen.', err);
                            },
                        });
                },
            });
    }

    private closeSaved(dokument?: BestellungExternDokument | null) {
        const result: InvoiceDeliverNoteSavedResult = {
            saved: true,
            kind: this.selectedKind,
            extraFiles: this.extraFiles,
            bestellung: this.bestellung,
            bedId: dokument?.bed_id ? Number(dokument.bed_id) : undefined,
            dokument: dokument || undefined,
        };
        this.dialogRef.close(result);
    }

    private onCommitError(err: any) {
        this.saving = false;
        this.messageService.errorMessage(
            'Speichern über Scan fehlgeschlagen – bitte Dokumenttyp manuell wählen.',
            err,
        );
        this.scanFailed = true;
    }

    private startScan() {
        const file = this.file;
        if (!file) {
            this.scanFailed = true;
            this.scanError = 'Keine Datei.';
            return;
        }

        this.scanning = true;
        this.scanFailed = false;
        const beId = this.bestellung?.bestellung_extern_id;
        this.invoiceKi
            .preview(file, {
                persistTarget: 'bestellung_extern',
                dokumentTyp: this.dokumentTypForPreview(),
                ...(beId ? { bestellungExternId: Number(beId) } : {}),
            })
            .subscribe({
                next: draft => {
                    this.scanning = false;
                    this.draft = draft;
                    this.warnings = Array.isArray(draft?.warnings) ? draft.warnings : [];
                    this.suggestedKind = this.kindFromDraft(draft);
                    if (!this.kindLocked) {
                        this.selectedKind = this.suggestedKind;
                    }
                    this.applySuggestedBestellung(draft);
                },
                error: err => {
                    this.scanning = false;
                    this.scanFailed = true;
                    this.scanError =
                        err?.error?.message ||
                        err?.message ||
                        'Beleg konnte nicht gelesen werden. Typ bitte manuell wählen.';
                },
            });
    }

    private dokumentTypForPreview(): number | undefined {
        if (this.selectedKind === 'invoice') {
            return 2;
        }
        if (this.selectedKind === 'deliveryNote') {
            return 1;
        }
        return undefined;
    }

    private applySuggestedBestellung(draft: WawiInvoiceDraftDto) {
        this.recomputeCandidateScores(draft);
        const suggested = Number(draft?.suggestedBestellungExternId) || 0;
        if (suggested > 0) {
            const fromList = this.assignmentOptions.find(c => Number(c.bestellung_extern_id) === suggested);
            if (fromList) {
                this.selectOrder(fromList, false);
                if (!(fromList.positionen?.length > 0)) {
                    this.loadBestellung(suggested, false);
                }
            } else {
                this.loadBestellung(suggested, true);
            }
        } else if (this.resolvedBestellung) {
            this.selectedBestellungId = Number(this.resolvedBestellung.bestellung_extern_id);
            this.matches = this.buildMatches(draft);
        } else {
            this.matches = this.buildMatches(draft);
        }
        this.loadCandidateOrders(draft);
    }

    private loadCandidateOrders(draft: WawiInvoiceDraftDto) {
        const seq = ++this.ordersLoadSeq;
        const lieferantId =
            Number(draft?.suggestedLieferantId) ||
            Number(this.resolvedBestellung?.lieferant_id) ||
            0;
        const artikelIds = this.ocrLines(draft)
            .map(l => Number(l.artikelId) || 0)
            .filter(id => id > 0);
        const uniqueArtikel = [...new Set(artikelIds)];

        const byLieferant$ =
            lieferantId > 0
                ? this.bestellungExternService.getOpenOrdersByLieferant(lieferantId, OPEN_ORDER_STATUS).pipe(
                      map(res => (Array.isArray(res?.records) ? res.records : [])),
                      catchError(() => of([] as BestellungExtern[])),
                  )
                : of([] as BestellungExtern[]);

        const byArtikel$ =
            uniqueArtikel.length > 0
                ? forkJoin(
                      uniqueArtikel.map(id =>
                          this.bestellungExternService
                              .getBestellungenByMaterialAndStatus(id, OPEN_ORDER_STATUS)
                              .pipe(
                                  map(res => (Array.isArray(res?.records) ? res.records : [])),
                                  catchError(() => of([] as BestellungExtern[])),
                              ),
                      ),
                  ).pipe(map(lists => lists.reduce((acc, cur) => acc.concat(cur), [] as BestellungExtern[])))
                : of([] as BestellungExtern[]);

        forkJoin([byLieferant$, byArtikel$]).subscribe({
            next: ([byLieferant, byArtikel]) => {
                if (seq !== this.ordersLoadSeq) {
                    return;
                }
                const merged = this.mergeOrders([
                    ...(this.data?.candidates || []),
                    ...byLieferant,
                    ...byArtikel,
                    ...(this.resolvedBestellung ? [this.resolvedBestellung] : []),
                ]);
                const filtered =
                    lieferantId > 0
                        ? merged.filter(
                              o =>
                                  !o.lieferant_id ||
                                  KieneUtils.sameId(o.lieferant_id, lieferantId) ||
                                  KieneUtils.sameId(
                                      o.bestellung_extern_id,
                                      draft?.suggestedBestellungExternId,
                                  ),
                          )
                        : merged;
                this.lieferantCandidates = filtered;
                this.recomputeCandidateScores(this.draft);
                this.ensureSelectionAfterCandidates(draft);
            },
        });
    }

    private ensureSelectionAfterCandidates(draft: WawiInvoiceDraftDto) {
        const suggested = Number(draft?.suggestedBestellungExternId) || 0;
        if (suggested > 0) {
            const found = this.assignmentOptions.find(c => Number(c.bestellung_extern_id) === suggested);
            if (found) {
                this.selectOrder(found, !(found.positionen?.length > 0));
                return;
            }
        }
        if (this.resolvedBestellung) {
            this.selectedBestellungId = Number(this.resolvedBestellung.bestellung_extern_id);
            this.matches = this.buildMatches(draft);
            return;
        }
        if (this.assignmentOptions.length === 1) {
            this.selectOrder(this.assignmentOptions[0], true);
            return;
        }
        if (this.assignmentOptions.length > 0) {
            this.selectOrder(this.assignmentOptions[0], true);
        }
    }

    private selectOrder(order: BestellungExtern, reloadDetails: boolean) {
        if (!order?.bestellung_extern_id) {
            return;
        }
        this.resolvedBestellung = order;
        this.selectedBestellungId = Number(order.bestellung_extern_id);
        this.matches = this.buildMatches(this.draft);
        if (reloadDetails) {
            this.loadBestellung(this.selectedBestellungId, false);
        }
    }

    private mergeOrders(orders: BestellungExtern[]): BestellungExtern[] {
        const byId = new Map<number, BestellungExtern>();
        for (const o of orders || []) {
            const id = Number(o?.bestellung_extern_id);
            if (!(id > 0)) {
                continue;
            }
            const prev = byId.get(id);
            if (!prev || (o.positionen?.length || 0) > (prev.positionen?.length || 0)) {
                byId.set(id, o);
            }
        }
        return [...byId.values()];
    }

    private loadBestellung(id: number, selectAfterLoad = true) {
        const params = new HttpParams().set('bestellung_extern_id', String(id));
        this.api.get(this.apiBaseUrl + 'bestellung_extern/read_one.php', params).subscribe({
            next: (row: BestellungExtern) => {
                if (!row?.bestellung_extern_id) {
                    return;
                }
                if (selectAfterLoad || Number(this.selectedBestellungId) === Number(row.bestellung_extern_id)) {
                    this.resolvedBestellung = row;
                    this.selectedBestellungId = Number(row.bestellung_extern_id);
                    this.matches = this.buildMatches(this.draft);
                }
                this.lieferantCandidates = this.mergeOrders([...this.lieferantCandidates, row]);
                this.recomputeCandidateScores(this.draft);
            },
            error: () => {
                this.messageService.infoMessage(
                    'Erkannte Bestellung konnte nicht geladen werden – bitte manuell zuordnen.',
                );
            },
        });
    }

    private recomputeCandidateScores(draft: WawiInvoiceDraftDto) {
        this.candidateScores.clear();
        const ocrIds = this.ocrLines(draft)
            .map(l => Number(l.artikelId) || 0)
            .filter(id => id > 0);
        if (ocrIds.length === 0) {
            return;
        }
        const ocrSet = new Set(ocrIds);
        for (const c of [...this.lieferantCandidates, ...(this.data?.candidates || []), this.resolvedBestellung]) {
            if (!c?.bestellung_extern_id) {
                continue;
            }
            const id = Number(c.bestellung_extern_id);
            const pos = c.positionen || [];
            let hits = 0;
            for (const p of pos) {
                if (ocrSet.has(Number(p.artikel_id))) {
                    hits++;
                }
            }
            if (hits > 0) {
                this.candidateScores.set(id, Math.max(this.candidateScores.get(id) || 0, hits));
            }
        }
    }

    private kindFromDraft(draft: WawiInvoiceDraftDto): WareneingangScanKind {
        const kind = (draft?.documentKind || '').toLowerCase();
        if (kind === 'gutschrift') {
            return 'credit';
        }
        if (kind === 'rechnung' || draft?.suggestedDokumentTyp === 2) {
            return 'invoice';
        }
        return 'deliveryNote';
    }

    private buildMatches(draft: WawiInvoiceDraftDto): WareneingangMatchRow[] {
        const positions = this.bestellung?.positionen || [];
        const ocrLines = this.ocrLines(draft);
        const used = new Set<number>();
        return positions.map(pos => {
            const lineIdx = this.findOcrLine(ocrLines, pos, used);
            const ocrMenge = lineIdx >= 0 ? ocrLines[lineIdx].menge : null;
            if (lineIdx >= 0) {
                used.add(lineIdx);
            }
            const matched = lineIdx >= 0;
            const qtyOk =
                ocrMenge == null ||
                ocrMenge === 0 ||
                ocrMenge === Number(pos.menge_soll) ||
                ocrMenge === Number(pos.menge_ist);
            return {
                artikel: pos.artikel || pos.artikel_me || 'Artikel',
                mengeSoll: Number(pos.menge_soll) || 0,
                mengeIst: Number(pos.menge_ist) || 0,
                ocrMenge,
                matched,
                qtyOk,
            };
        });
    }

    private ocrLines(
        draft: WawiInvoiceDraftDto,
    ): Array<{ artikelId?: number; bezeichnung?: string; menge: number | null; netto: number | null }> {
        const isPlaceholder = (bezeichnung?: string) => {
            const d = (bezeichnung || '').trim().toLowerCase();
            return (
                d.startsWith('manuell prüfen') ||
                d.includes('extract ohne positionen') ||
                d.includes('extract fehlgeschlagen') ||
                d.includes('extract ohne betrag')
            );
        };
        const mapRow = (raw: Record<string, unknown>) => {
            const mengeRaw = raw['menge'];
            const nettoRaw =
                raw['netto'] ??
                raw['kostenNetto'] ??
                raw['kosten_netto'] ??
                raw['unitPrice'] ??
                raw['einzelpreis'] ??
                raw['netUnitPrice'];
            const bez = raw['bezeichnung'] ?? raw['description'] ?? raw['artikelMe'] ?? raw['artikel_me'];
            return {
                artikelId: raw['artikelId'] != null ? Number(raw['artikelId']) : undefined,
                bezeichnung: bez != null ? String(bez) : undefined,
                menge: mengeRaw != null && mengeRaw !== '' ? Number(mengeRaw) : null,
                netto: nettoRaw != null && nettoRaw !== '' ? Number(nettoRaw) : null,
            };
        };

        const fromPositionen = Array.isArray(draft?.positionen)
            ? draft.positionen.map(raw => mapRow(raw as Record<string, unknown>)).filter(l => !isPlaceholder(l.bezeichnung))
            : [];
        const fromExtract = (draft?.extract?.lines || [])
            .map(l => mapRow(l as unknown as Record<string, unknown>))
            .filter(l => !isPlaceholder(l.bezeichnung));

        // Wareneingang-Positionen oft nur Menge – Extract-Zeilen mit Netto ergänzen/mergen
        if (fromPositionen.length === 0) {
            return fromExtract;
        }
        const posHaveNetto = fromPositionen.some(l => l.netto != null && l.netto > 0);
        if (posHaveNetto || fromExtract.length === 0) {
            return fromPositionen;
        }
        // Netto aus Extract auf Mengen-Zeilen legen, wenn 1:1
        if (fromPositionen.length === 1 && fromExtract.filter(l => l.netto != null && l.netto > 0).length === 1) {
            const n = fromExtract.find(l => l.netto != null && l.netto > 0)!;
            return [
                {
                    ...fromPositionen[0],
                    netto: n.netto,
                    bezeichnung: fromPositionen[0].bezeichnung || n.bezeichnung,
                },
            ];
        }
        return [...fromPositionen, ...fromExtract];
    }

    private findOcrLine(
        lines: Array<{ artikelId?: number; bezeichnung?: string; menge: number | null }>,
        pos: BestellungExternPosition,
        used: Set<number>,
    ): number {
        const byId = lines.findIndex(
            (l, i) => !used.has(i) && l.artikelId && Number(l.artikelId) === Number(pos.artikel_id),
        );
        if (byId >= 0) {
            return byId;
        }
        const name = (pos.artikel || '').trim().toLowerCase();
        if (!name) {
            return -1;
        }
        return lines.findIndex((l, i) => {
            if (used.has(i) || !l.bezeichnung) {
                return false;
            }
            const n = l.bezeichnung.trim().toLowerCase();
            return n.includes(name) || name.includes(n);
        });
    }

    private loadExistingInvoicePdf(dokument: BestellungExternDokument) {
        this.revokePdfUrl();
        this.pdfLoading = true;
        this.pdfSrc = null;
        const params = new HttpParams().set('bed_id', String(dokument.bed_id));
        this.http
            .get(this.apiBaseUrl + 'bestellung_extern_dokument/read_pdf_rechnung.php', {
                params,
                responseType: 'arraybuffer',
            })
            .subscribe({
                next: buf => {
                    this.pdfSrc = buf?.byteLength ? new Uint8Array(buf) : null;
                    this.pdfLoading = false;
                },
                error: () => {
                    this.pdfLoading = false;
                    this.pdfSrc = null;
                },
            });
    }

    private initPdfViewer() {
        this.revokePdfUrl();
        const file = this.file;
        if (!file) {
            this.pdfSrc = null;
            return;
        }
        this.pdfLoading = true;
        file
            .arrayBuffer()
            .then(buf => {
                this.pdfSrc = new Uint8Array(buf);
                this.pdfObjectUrl = URL.createObjectURL(file);
                this.pdfLoading = false;
            })
            .catch(() => {
                this.pdfLoading = false;
                this.pdfSrc = null;
            });
    }

    private revokePdfUrl() {
        if (this.pdfObjectUrl) {
            URL.revokeObjectURL(this.pdfObjectUrl);
            this.pdfObjectUrl = null;
        }
    }
}
